import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

/**
 * An in-app confirmation. The browser's own confirm() is never used: it cannot be translated, it
 * cannot be styled, and it blocks the page — a rule carried over from SocialAI, where it was
 * learned the hard way.
 */
export default function ConfirmDialog({ open, title, body, confirmLabel, destructive, onConfirm, onCancel }) {
  const { t } = useTranslation()
  const confirmRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined

    confirmRef.current?.focus()

    const onKey = (event) => {
      if (event.key !== 'Escape') return

      // Escape answers the question, not the thing that asked it: the preview underneath listens
      // for the same key, and without this both close at once.
      event.stopPropagation()
      onCancel()
    }

    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, onCancel])

  if (!open) return null

  // Rendered into the body rather than where it was written. A confirmation lives above whatever
  // raised it, and "whatever raised it" is often a modal that comes later in the markup — which is
  // how the question ended up painted underneath the preview it was asked about.
  return createPortal(
    <div className="overlay overlay--ask" role="presentation" onClick={onCancel}>
      <div
        className="dialog"
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <h3>{title}</h3>
        {body && <p className="muted">{body}</p>}

        <div className="dialog__actions">
          <button type="button" className="button button--quiet" onClick={onCancel}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            ref={confirmRef}
            className={destructive ? 'button button--danger' : 'button'}
            onClick={onConfirm}
          >
            {confirmLabel ?? t('common.confirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
